import { useState, useMemo, useEffect, useRef } from "react";
import axios from "axios";
import api from "../../lib/api";
import { toast } from "sonner";
import { useNavigate, useSearchParams } from "react-router-dom";
import HelpButton from "../HelpButton";
import { API } from "../../App";
import { hasPerm } from "../../utils/permissions";
import { getDealProbability } from "../../config/uiHelpers";
import { useDealsPipeline } from "./hooks/useDealsPipeline";
import DealsToolbar from "./DealsToolbar";
import DealsFilters from "./DealsFilters";
import DealColumn from "./DealColumn";

const STAGES = [
  { key: "prospect", label: "Prospect", color: "#475569" },
  { key: "negotiation", label: "Negotiation", color: "#F59E0B" },
  { key: "proposal", label: "Proposal", color: "#002FA7" },
  { key: "sales_order_created", label: "Sales Order Created", color: "#7C3AED" },
  { key: "closed_won", label: "Closed Won", color: "#10B981" },
  { key: "closed_lost", label: "Closed Lost", color: "#DC2626" },
];

// URL <-> state keys (kept short for shareable links)
const URL_KEYS = {
  q: "q", bde: "bde", manager: "mgr", stage: "stage",
  range: "range", basis: "basis", from: "from", to: "to",
  sortBy: "sb", sortDir: "sd",
};

// Deals Board v2 — orchestrator. Owns: layout, RBAC gates, create-deal modal,
// mutations (move stage, create quotation, delete). Data/sort/filter logic lives in the hook.
export default function DealsBoard({ user }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const pipeline = useDealsPipeline(user);
  const { deals, contacts, loading, search, setSearch, filters, setFilters, sort, setSort, refresh, bdeOptions, managerOptions, SORT_OPTIONS } = pipeline;

  // One-time hydrate from URL on mount.
  const hydratedRef = useRef(false);
  useEffect(() => {
    if (hydratedRef.current) return;
    hydratedRef.current = true;
    const sp = searchParams;
    const next = {
      assigned_to_id: sp.get(URL_KEYS.bde) || "",
      manager_id: sp.get(URL_KEYS.manager) || "",
      stage: sp.get(URL_KEYS.stage) || "",
      date_range: sp.get(URL_KEYS.range) || "",
      date_basis: sp.get(URL_KEYS.basis) || "created_at",
      from_date: sp.get(URL_KEYS.from) || "",
      to_date: sp.get(URL_KEYS.to) || "",
    };
    setFilters(next);
    const q = sp.get(URL_KEYS.q);
    if (q) setSearch(q);
    const sb = sp.get(URL_KEYS.sortBy);
    const sd = sp.get(URL_KEYS.sortDir);
    if (sb || sd) setSort({ by: sb || "updated_at", dir: sd || "desc" });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync filters/sort/search to URL (replace, not push, to avoid history bloat).
  useEffect(() => {
    if (!hydratedRef.current) return;
    const next = new URLSearchParams();
    if (search?.trim()) next.set(URL_KEYS.q, search.trim());
    if (filters.assigned_to_id) next.set(URL_KEYS.bde, filters.assigned_to_id);
    if (filters.manager_id) next.set(URL_KEYS.manager, filters.manager_id);
    if (filters.stage) next.set(URL_KEYS.stage, filters.stage);
    if (filters.date_range) {
      next.set(URL_KEYS.range, filters.date_range);
      if (filters.date_basis && filters.date_basis !== "created_at") next.set(URL_KEYS.basis, filters.date_basis);
      if (filters.date_range === "custom") {
        if (filters.from_date) next.set(URL_KEYS.from, filters.from_date);
        if (filters.to_date) next.set(URL_KEYS.to, filters.to_date);
      }
    }
    if (sort.by && sort.by !== "updated_at") next.set(URL_KEYS.sortBy, sort.by);
    if (sort.dir && sort.dir !== "desc") next.set(URL_KEYS.sortDir, sort.dir);
    setSearchParams(next, { replace: true });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, sort, search]);

  const [showCreate, setShowCreate] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [quotingDealId, setQuotingDealId] = useState("");
  const [newDeal, setNewDeal] = useState({
    title: "", contact_id: "", contact_name: "", value: "", stage: "prospect",
    probability: 20, assigned_to: user?.name || "", notes: "", expected_close: "",
  });

  const canCreateDeal = hasPerm(user, "deals.create");
  const canEditDeal = hasPerm(user, "deals.edit");
  const canDeleteDeal = hasPerm(user, "deals.delete");

  const filtersActive = !!(filters.assigned_to_id || filters.manager_id || filters.stage || filters.date_range || (search || "").trim());

  // Group deals by stage
  const dealsByStage = useMemo(() => {
    const map = {};
    STAGES.forEach((s) => (map[s.key] = []));
    (deals || []).forEach((d) => {
      const k = d.stage in map ? d.stage : "prospect";
      map[k].push(d);
    });
    return map;
  }, [deals]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newDeal.title.trim()) {
      toast.error("Deal title is required");
      return;
    }
    try {
      const contact = contacts.find((c) => c.contact_id === newDeal.contact_id);
      const valueNum = parseFloat(newDeal.value);
      await api.post(`${API}/crm/deals`, {
        ...newDeal,
        value: Number.isFinite(valueNum) ? valueNum : 0,
        contact_id: newDeal.contact_id || "",
        contact_name: contact?.name || newDeal.contact_name || "",
        requester_user_id: user?.user_id || "",
        requester_name: user?.name || "",
        requester_role: user?.role || "",
      });
      toast.success("Deal created!");
      setShowCreate(false);
      setNewDeal({ title: "", contact_id: "", contact_name: "", value: "", stage: "prospect", probability: 20, assigned_to: user?.name || "", notes: "", expected_close: "" });
      refresh();
    } catch (error) {
      const msg = error?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Failed to create deal");
    }
  };

  const moveStage = async (dealId, newStage) => {
    const prob = getDealProbability(newStage);
    try {
      await api.put(`${API}/crm/deals/${dealId}`, {
        stage: newStage,
        probability: prob,
        requester_user_id: user?.user_id || "",
        requester_name: user?.name || "",
        requester_role: user?.role || "",
      });
      toast.success("Deal stage updated!");
      refresh();
    } catch (error) {
      toast.error("Failed to update deal");
    }
  };

  const deleteDeal = async (dealId) => {
    try {
      await api.delete(`${API}/crm/deals/${dealId}`);
      toast.success("Deal deleted!");
      refresh();
    } catch (error) {
      toast.error("Failed to delete deal");
    }
  };

  const createQuotationFromDeal = async (deal) => {
    if (deal.linked_quotation_id) {
      toast.success(`Opening linked quotation ${deal.linked_quotation_number || ""}`);
      navigate("/billing");
      return;
    }
    setQuotingDealId(deal.deal_id);
    try {
      const params = new URLSearchParams({
        requester_role: user?.role || "",
        requester_user_id: user?.user_id || "",
        requester_name: user?.name || "",
      });
      const r = await api.post(`${API}/crm/deals/${deal.deal_id}/convert-to-quotation?${params.toString()}`, { doc_type: "estimation" });
      const num = r.data?.doc?.number || "";
      toast.success(r.data?.message?.includes("already") ? `Existing ${num} opened` : `Quotation ${num} created`);
      refresh();
      setTimeout(() => navigate("/billing"), 600);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create quotation");
    } finally {
      setQuotingDealId("");
    }
  };

  const totalCount = pipeline.rawDeals.length;
  const filteredCount = deals.length;

  return (
    <div>
      <div className="page-header-sticky">
        <div className="px-6 lg:px-8 pt-5 pb-4">
          <div className="flex flex-wrap justify-between items-start mb-4 gap-3">
            <div>
              <div className="flex items-center">
                <h1 className="text-4xl sm:text-5xl tracking-tighter leading-none font-bold mb-1" style={{ fontFamily: "Outfit, sans-serif" }}>
                  Deals
                </h1>
                <HelpButton moduleKey="deal" />
              </div>
              <p className="text-sm leading-relaxed text-slate-600">Operational pipeline · ownership · aging · follow-ups</p>
            </div>
          </div>

          <DealsToolbar
            search={search}
            onSearchChange={setSearch}
            sort={sort}
            onSortChange={setSort}
            sortOptions={SORT_OPTIONS}
            totalCount={totalCount}
            filteredCount={filteredCount}
            onNewDeal={() => setShowCreate((v) => !v)}
            canCreate={canCreateDeal}
            onToggleFilters={() => setShowFilters((v) => !v)}
            filtersActive={filtersActive}
            filtersOpen={showFilters}
          />

          {showFilters && (
            <DealsFilters
              filters={filters}
              onFiltersChange={setFilters}
              bdeOptions={bdeOptions}
              managerOptions={managerOptions}
              onClearAll={() => {
                setFilters({ assigned_to_id: "", manager_id: "", stage: "", date_range: "", date_basis: "created_at", from_date: "", to_date: "" });
                setSearch("");
              }}
            />
          )}
        </div>
      </div>

      <div className="px-6 lg:px-8 pt-4 pb-8">
        {showCreate && (
        <div data-testid="create-deal-form" className="bg-white border border-slate-200 rounded-none p-6 mb-4 shadow-sm">
          <h3 className="text-lg font-semibold mb-4" style={{ fontFamily: "Outfit, sans-serif" }}>
            New Deal
          </h3>
          <form onSubmit={handleCreate} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 block mb-1">Deal Title</label>
              <input data-testid="deal-title-input" type="text" value={newDeal.title} onChange={(e) => setNewDeal({ ...newDeal, title: e.target.value })} className="rounded-none bg-white border border-slate-300 focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] p-2.5 w-full outline-none text-sm" required />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 block mb-1">Contact</label>
              <select data-testid="deal-contact-select" value={newDeal.contact_id} onChange={(e) => setNewDeal({ ...newDeal, contact_id: e.target.value })} className="rounded-none bg-white border border-slate-300 focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] p-2.5 w-full outline-none text-sm">
                <option value="">Select Contact (optional)</option>
                {contacts.map((c) => (
                  <option key={c.contact_id} value={c.contact_id}>
                    {c.name} - {c.company}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 block mb-1">Value (₹)</label>
              <input data-testid="deal-value-input" type="number" step="0.01" min="0" value={newDeal.value} onChange={(e) => setNewDeal({ ...newDeal, value: e.target.value })} className="rounded-none bg-white border border-slate-300 focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] p-2.5 w-full outline-none text-sm" placeholder="0" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 block mb-1">Expected Close Date</label>
              <input data-testid="deal-close-date-input" type="date" value={newDeal.expected_close} onChange={(e) => setNewDeal({ ...newDeal, expected_close: e.target.value })} className="rounded-none bg-white border border-slate-300 focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] p-2.5 w-full outline-none text-sm" />
            </div>
            <div className="md:col-span-2">
              <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 block mb-1">Notes</label>
              <textarea data-testid="deal-notes-input" value={newDeal.notes} onChange={(e) => setNewDeal({ ...newDeal, notes: e.target.value })} className="rounded-none bg-white border border-slate-300 focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] p-2.5 w-full outline-none text-sm" rows="3" />
            </div>
            <div className="md:col-span-2">
              <button data-testid="submit-deal-btn" type="submit" className="rounded-none bg-[#002FA7] text-white hover:bg-[#00227A] px-6 py-2.5 font-medium text-sm">
                Create Deal
              </button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="loading-spinner"></div>
        </div>
      ) : (
        <div data-testid="deals-pipeline" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
          {STAGES.map((stage) => (
            <DealColumn
              key={stage.key}
              stage={stage}
              deals={dealsByStage[stage.key] || []}
              canEdit={canEditDeal}
              canDelete={canDeleteDeal}
              quotingDealId={quotingDealId}
              onMoveStage={moveStage}
              onCreateQuotation={createQuotationFromDeal}
              onDelete={deleteDeal}
            />
          ))}
        </div>
      )}
      </div>
    </div>
  );
}
